// The 'env' constant is defined using an Immediately Invoked Function Expression (IIFE).
// This design pattern allows the creation of a new lexical scope, allowing complex conditional logic to be used to define the 'env' constant.
// The IIFE is executed right after its definition, initializing the 'env' constant with the appropriate environment variables.
// Note that these values are known public values, that can only be used by code running on a whitelisted set of domains, so don't freak out!
export const env = (() => {
	const url = typeof window !== "undefined" ? window.location.origin : "";

	if (url.includes("://cognition.cerebrum.com") || url.includes("://cognition.jdp1.com")) {
		return {
			BRANCH_IO_URL: "usevid.app",
			COGNITION_URL: url.includes("://cognition.cerebrum.com")
				? "https://cognition.cerebrum.com/"
				: "https://cognition.jdp1.com/",
			CORTEX_URL: "https://cortex.cerebrum.com/",
			GOOGLE_MAPS_API_KEY: "AIzaSyBeUbmSUzQ7WIy3uPFvVw7GFwQHu79zSSA",
			MIXPANEL_TOKEN: "6b5d9f26a6ce3d171248b78a6e3283dd",
			API_URL: "https://api.cerebrum.com",
			PRIVACY_POLICY_URL: "https://cerebrum.com/legal/privacy/",
			SERVER_URL: "https://api.cerebrum.com",
			TERMS_AND_CONDITIONS_URL: "https://www.cerebrum.com/terms",
			SOCKET_URL: "https://socket.api.cerebrum.com",
			POSTHOG_API_KEY: "phc_7hcnW6yqZgMMiaOADyKk0sovDry4tNH7AxmykQFOzca",
		};
	}

	if (url.includes("://cognition.demo.cerebrum.com")) {
		return {
			BRANCH_IO_URL: "usevidapp.com",
			COGNITION_URL: "https://cognition.demo.cerebrum.com/",
			CORTEX_URL: "https://cortex.demo.cerebrum.com/",
			GOOGLE_MAPS_API_KEY: "AIzaSyBPLyM85ba7A-v90OaIAfcN9lrVK3XFuPE",
			API_URL: "https://api.demo.cerebrum.com",
			PRIVACY_POLICY_URL: "https://cerebrum.com/legal/privacy/",
			SERVER_URL: "https://api.demo.cerebrum.com",
			TERMS_AND_CONDITIONS_URL: "https://www.cerebrum.com/terms",
			SOCKET_URL: "https://socket.api.demo.cerebrum.com",
			POSTHOG_API_KEY: "phc_whV3zx0JRqAXjD5tUkkzhXt55JHCfSgF0nWh4RqrtDz",
		};
	}

	if (url.includes("://cognition.staging.cerebrum.com") || url.includes("://cognition.staging.jdp1.com")) {
		return {
			BRANCH_IO_URL: "usevidapp.com",
			COGNITION_URL: url.includes("://cognition.staging.cerebrum.com")
				? "https://cognition.staging.cerebrum.com/"
				: "https://cognition.staging.jdp1.com/",
			CORTEX_URL: "https://cortex.staging.cerebrum.com/",
			GOOGLE_MAPS_API_KEY: "AIzaSyBWx65P96LSAjR8w9fkbDatb_JUemD24g8",
			MIXPANEL_TOKEN: "a2bcb8af24804ab5d37d86fedf89fd96",
			API_URL: "https://api.staging.cerebrum.com",
			PRIVACY_POLICY_URL: "https://cerebrum.com/legal/privacy/",
			SERVER_URL: "https://api.staging.cerebrum.com",
			TERMS_AND_CONDITIONS_URL: "https://www.cerebrum.com/terms",
			SOCKET_URL: "https://socket.api.staging.cerebrum.com",
			POSTHOG_API_KEY: "phc_pHsVkRNbaQLpIzntUdLJknJ4edAKxhu8HeqLD2EeJr8",
		};
	}

	// Axon previews use <app>-<preview>.axon.dev.cerebrum.com hostnames and development public keys.
	const preview = url.match(/:\/\/cognition-(pv-[a-z0-9-]+)\.axon\.dev\.cerebrum\.com$/);
	if (preview) {
		const host = (app: string) => `https://${app}-${preview[1]}.axon.dev.cerebrum.com`;
		return {
			BRANCH_IO_URL: "usevidapp.com",
			COGNITION_URL: `${host("cognition")}/`,
			CORTEX_URL: `${host("cortex")}/`,
			GOOGLE_MAPS_API_KEY: "AIzaSyBPLyM85ba7A-v90OaIAfcN9lrVK3XFuPE",
			MIXPANEL_TOKEN: "92b85ee63d27638904055c91004545fa",
			API_URL: host("cortex-api"),
			ORY_SDK_URL: host("auth-kratos"),
			PRIVACY_POLICY_URL: "https://cerebrum.com/legal/privacy/",
			SERVER_URL: host("cortex-api"),
			TERMS_AND_CONDITIONS_URL: "https://www.cerebrum.com/terms",
			SOCKET_URL: host("socket"),
			POSTHOG_API_KEY: "phc_uV7P39hwcpuL6j4ohJrvM3zg3Lu9HqtNUiu6tn3dbEPG",
		};
	}

	// In local mode we want to route to local domains.
	// ORY_SDK_URL must point at auth-kratos directly so the continuity cookie is set for the same
	// domain as the OIDC callback (auth-kratos.local.cerebrum.com); otherwise the callback fails.
	if (url.includes("://cognition.local.cerebrum.com") || url.includes("://cognition.local.jdp1.com")) {
		return {
			BRANCH_IO_URL: "usevidapp.com",
			COGNITION_URL: url.includes("://cognition.local.cerebrum.com")
				? "https://cognition.local.cerebrum.com/"
				: "https://cognition.local.jdp1.com/",
			CORTEX_URL: "https://cortex.local.cerebrum.com/",
			GOOGLE_MAPS_API_KEY: "AIzaSyBPLyM85ba7A-v90OaIAfcN9lrVK3XFuPE",
			MIXPANEL_TOKEN: "92b85ee63d27638904055c91004545fa",
			API_URL: "https://cortex-api.local.cerebrum.com",
			ORY_SDK_URL: "https://auth-kratos.local.cerebrum.com",
			PRIVACY_POLICY_URL: "https://cerebrum.com/legal/privacy/",
			SERVER_URL: "https://cortex-api.local.cerebrum.com",
			TERMS_AND_CONDITIONS_URL: "https://www.cerebrum.com/terms",
			SOCKET_URL: "https://socket.local.cerebrum.com",
			POSTHOG_API_KEY: "phc_whV3zx0JRqAXjD5tUkkzhXt55JHCfSgF0nWh4RqrtDz",
		};
	}

	// Otherwise default to values for development environment
	return {
		BRANCH_IO_URL: "usevidapp.com",
		COGNITION_URL: url.includes("://cognition.dev.cerebrum.com")
			? "https://cognition.dev.cerebrum.com/"
			: "https://cognition.dev.jdp1.com/",
		CORTEX_URL: "https://cortex.dev.cerebrum.com/",
		GOOGLE_MAPS_API_KEY: "AIzaSyBPLyM85ba7A-v90OaIAfcN9lrVK3XFuPE",
		MIXPANEL_TOKEN: "92b85ee63d27638904055c91004545fa",
		API_URL: "https://api.dev.cerebrum.com",
		PRIVACY_POLICY_URL: "https://cerebrum.com/legal/privacy/",
		SERVER_URL: "https://api.dev.cerebrum.com",
		TERMS_AND_CONDITIONS_URL: "https://www.cerebrum.com/terms",
		SOCKET_URL: "https://socket.api.dev.cerebrum.com",
		POSTHOG_API_KEY: "phc_whV3zx0JRqAXjD5tUkkzhXt55JHCfSgF0nWh4RqrtDz",
	};
})();

export const graphqlEndpointUrl = `${env.SERVER_URL}/graphql`;
